// Status → Pill tone/label maps for the money screens, plus the one rule the
// list applies itself: an invoice is OVERDUE when it is still owed on and its
// due date has passed. Compared as YYYY-MM-DD strings, so no Date parse and
// no day-out error (dateOnlyBoundaries.test.js).

export const INVOICE_STATUS = {
  draft:     { tone: 'neutral', label: 'Draft' },
  sent:      { tone: 'sched',   label: 'Sent' },
  partial:   { tone: 'warn',    label: 'Partial' },
  paid:      { tone: 'ok',      label: 'Paid' },
  overdue:   { tone: 'stop',    label: 'Overdue' },
  cancelled: { tone: 'neutral', label: 'Cancelled' },
};

/** sent/partial with a due_date strictly before today (both YYYY-MM-DD). */
export function isInvoiceOverdue(invoice, today) {
  if (!invoice) return false;
  const status = invoice.status || 'draft';
  if (status !== 'sent' && status !== 'partial') return false;
  if (!invoice.due_date || !today) return false;
  return String(invoice.due_date).slice(0, 10) < String(today).slice(0, 10);
}

/** The status key the list shows — 'overdue' overrides sent/partial once past due. */
export function invoiceStatusKey(invoice, today) {
  if (isInvoiceOverdue(invoice, today)) return 'overdue';
  return (invoice && invoice.status) || 'draft';
}

export function invoicePill(invoice, today) {
  const key = invoiceStatusKey(invoice, today);
  return INVOICE_STATUS[key] || { tone: 'neutral', label: key };
}

export const ESTIMATE_STATUS = {
  draft:            { tone: 'neutral', label: 'Draft' },
  pending_approval: { tone: 'warn',    label: 'Awaiting approval' },
  approved:         { tone: 'sched',   label: 'Approved' },
  sent:             { tone: 'sched',   label: 'Sent' },
  accepted:         { tone: 'ok',      label: 'Accepted' },
  declined:         { tone: 'stop',    label: 'Declined' },
  expired:          { tone: 'neutral', label: 'Expired' },
};

export function estimatePill(estimate) {
  const key = (estimate && estimate.estimate_status) || 'draft';
  return ESTIMATE_STATUS[key] || { tone: 'neutral', label: key };
}

export const PAYSTUB_STATUS = {
  pending: { tone: 'warn', label: 'Pending' },
  paid:    { tone: 'ok',   label: 'Paid' },
};

export function paystubPill(paystub) {
  const key = (paystub && paystub.status) || 'pending';
  return PAYSTUB_STATUS[key] || { tone: 'neutral', label: key };
}

/**
 * Where a timesheet IS on its way to being paid — one word, not two columns.
 *
 * Owner, 2026-09-28: "I need a clear view on timesheets for approvals and
 * making sure things got processed properly." Payroll's list printed the word
 * the server stored ("submitted") and nothing about the paystub, so a week
 * could be approved and never paid and the list read the same. GET
 * /api/timesheets now carries the sheet's newest paystub (paystub_id,
 * paystub_status), and this folds the two into the stage a person asks about.
 *
 * The words match the Approvals list where they overlap (Draft, To approve,
 * Rejected) — Office keeps its own mapping rather than importing Dispatch's,
 * and this one has stages Approvals does not.
 */
// `label` is the PILL, and a pill does not wrap: measured at 390px,
// "APPROVED · NOT PAID" was cut to "APPROVED · NOT I" beside a name on a
// card. So the pill says only what differs — every stage past To approve is
// an approved week — and `phrase` is the longer wording the list's summary
// sentence uses, where there is room to say it whole.
export const TIMESHEET_STAGE = {
  draft:     { tone: 'neutral', label: 'Draft',      phrase: 'draft' },
  submitted: { tone: 'now',     label: 'To approve', phrase: 'to approve' },
  rejected:  { tone: 'stop',    label: 'Rejected',   phrase: 'rejected' },
  unstubbed: { tone: 'warn',    label: 'No paystub', phrase: 'approved with no paystub' },
  unpaid:    { tone: 'sched',   label: 'Not paid',   phrase: 'approved, not paid' },
  paid:      { tone: 'ok',      label: 'Paid',       phrase: 'paid' },
};

export function timesheetStageKey(sheet) {
  const status = (sheet && sheet.status) || 'draft';
  if (status !== 'approved') return TIMESHEET_STAGE[status] ? status : 'draft';
  if (!sheet.paystub_id) return 'unstubbed';
  return sheet.paystub_status === 'paid' ? 'paid' : 'unpaid';
}

export function timesheetStagePill(sheet) {
  return TIMESHEET_STAGE[timesheetStageKey(sheet)];
}

/** REBUILD_API §3: a $0 line is a line nobody has priced yet. */
export function needsPrice(item) {
  if (!item) return false;
  const v = Number(item.unit_price);
  return !Number.isFinite(v) || v <= 0;
}
